// Google Ads › Kampanyalar ve Meta Ads › Kampanyalar.
// Üstte kampanya türü seçimi, Google Ads panelindeki gibi metrik kutucuklu günlük grafik,
// altında seçili türün kampanya tablosu. Google'da Performance Max / Display / Uygulama
// seçilince o türe özel analiz (eski "Derin Analiz" ekranları) tablonun altında açılır.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { T, MONO } from '../poyais-ui/theme';
import { useLang, addTranslations } from '../i18n';
import { exportCsv } from '../poyais-ui/export';
import { Panel, Segments, DataTable, Col, NameCell, StatusPill, Loading, Muted, money, num, pct, roasTxt, roasColor, useMobile } from './ui';
import GoogleTypeDetail from './GoogleTypeDetail';

const API = '/api/v1';

addTranslations({
  'Tümü': 'All', 'Arama': 'Search', 'Alışveriş': 'Shopping', 'Video': 'Video', 'Uygulama': 'App', 'Akıllı': 'Smart', 'Yerel': 'Local', 'Otel': 'Hotel', 'Diğer': 'Other',
  'Satış': 'Sales', 'Trafik': 'Traffic', 'Etkileşim': 'Engagement', 'Bilinirlik': 'Awareness', 'Potansiyel müşteri': 'Leads', 'Uygulama tanıtımı': 'App promotion',
  'Kampanya Türü': 'Campaign type', 'Harcama': 'Spend', 'Dönüşüm': 'Conversions', 'Dönüşüm değeri': 'Conv. value', 'Tıklama': 'Clicks', 'Gösterim': 'Impressions',
  'Satın alma': 'Purchases', 'Gelir': 'Revenue', 'Erişim': 'Reach', 'Sonuç': 'Results', 'Tıklama oranı': 'CTR', 'Dönüşüm başı maliyet': 'Cost / conv.',
  'Kampanya': 'Campaign', 'Durum': 'Status', 'Kampanyalar': 'Campaigns', 'Dışa Aktar': 'Export', 'Kampanya ara…': 'Search campaigns…',
  'Harcaması olanlar': 'With spend', 'Tüm Durumlar': 'All Statuses', 'Aktif Değil': 'Not Active', 'Harcaması olmayanları da göster': 'Show campaigns without spend', 'Harcama Dağılımı': 'Spend split', 'Kampanya türlerine göre bu dönemin harcaması.': 'This period’s spend by campaign type.',
  'Grafikte görmek istediğin en fazla iki metriği seç.': 'Pick up to two metrics to plot.', 'Günlük performans': 'Daily performance',
  'Bu dönemde bu türde kampanya yok.': 'No campaigns of this type in this period.', 'Veriler alınamadı.': 'Could not load data.',
  'Dönüşüm sütunları yalnızca kampanyanın teklifte kullandığı dönüşüm hedeflerini sayar.': 'Conversion columns count only the goals the campaign bids on.',
  'Satın alma ve gelir Meta pikselinin satın alma olayıdır; "Sonuç" kampanyanın kendi optimizasyon hedefidir. İkisi toplanmaz.': 'Purchases and revenue come from the Meta pixel purchase event; “Results” is the campaign’s own optimisation goal. They are not added together.',
  'Dönüşümleri artır': 'Maximize conversions', 'Dönüşüm değerini artır': 'Maximize conv. value', 'Hedef ROAS': 'Target ROAS', 'Hedef EBM': 'Target CPA',
  'Tıklamaları artır': 'Maximize clicks', 'Manuel TBM': 'Manual CPC', 'Hedef gösterim payı': 'Target impression share', 'Manuel GBM': 'Manual CPM', 'Hedef GBM': 'Target CPM',
  'Geliştirilmiş TBM': 'Enhanced CPC', 'Görüntüleme başı maliyet': 'CPV', 'Hedef görüntüleme maliyeti': 'Target CPV', 'Hedef TBM': 'Target CPC', 'Komisyon': 'Commission', 'Yüzde TBM': 'Percent CPC',
});

type Platform = 'google' | 'meta';

const G_TYPES: [string, string][] = [['SEARCH', 'Arama'], ['PERFORMANCE_MAX', 'Performance Max'], ['SHOPPING', 'Alışveriş'], ['DISPLAY', 'Display'], ['VIDEO', 'Video'],
  ['DEMAND_GEN', 'Demand Gen'], ['MULTI_CHANNEL', 'Uygulama'], ['SMART', 'Akıllı'], ['LOCAL', 'Yerel'], ['HOTEL', 'Otel']];
const M_OBJ: Record<string, string> = {
  OUTCOME_SALES: 'SALES', CONVERSIONS: 'SALES', PRODUCT_CATALOG_SALES: 'SALES', OUTCOME_TRAFFIC: 'TRAFFIC', LINK_CLICKS: 'TRAFFIC',
  OUTCOME_ENGAGEMENT: 'ENGAGEMENT', POST_ENGAGEMENT: 'ENGAGEMENT', PAGE_LIKES: 'ENGAGEMENT', MESSAGES: 'ENGAGEMENT', EVENT_RESPONSES: 'ENGAGEMENT', VIDEO_VIEWS: 'ENGAGEMENT',
  OUTCOME_AWARENESS: 'AWARENESS', REACH: 'AWARENESS', BRAND_AWARENESS: 'AWARENESS', OUTCOME_LEADS: 'LEADS', LEAD_GENERATION: 'LEADS', OUTCOME_APP_PROMOTION: 'APP', APP_INSTALLS: 'APP',
};
const M_TYPES: [string, string][] = [['SALES', 'Satış'], ['TRAFFIC', 'Trafik'], ['ENGAGEMENT', 'Etkileşim'], ['AWARENESS', 'Bilinirlik'], ['LEADS', 'Potansiyel müşteri'], ['APP', 'Uygulama tanıtımı']];
const BIDDING: Record<string, string> = {
  MAXIMIZE_CONVERSIONS: 'Dönüşümleri artır', MAXIMIZE_CONVERSION_VALUE: 'Dönüşüm değerini artır', TARGET_ROAS: 'Hedef ROAS', TARGET_CPA: 'Hedef EBM',
  TARGET_SPEND: 'Tıklamaları artır', MANUAL_CPC: 'Manuel TBM', TARGET_IMPRESSION_SHARE: 'Hedef gösterim payı', MANUAL_CPM: 'Manuel GBM', TARGET_CPM: 'Hedef GBM',
  ENHANCED_CPC: 'Geliştirilmiş TBM', MANUAL_CPV: 'Görüntüleme başı maliyet', TARGET_CPV: 'Hedef görüntüleme maliyeti', TARGET_CPC: 'Hedef TBM',
  COMMISSION: 'Komisyon', PERCENT_CPC: 'Yüzde TBM',
};
const TYPE_COLORS = ['#C9A84C', '#3b82f6', '#16a37f', '#a855f7', '#ef4444', '#f97316', '#0ea5e9', '#64748b', '#eab308', '#14b8a6'];

type Metric = { key: string; label: string; kind: 'money' | 'num' | 'roas' };

const MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function KampanyalarPage({ platform, start, end, config, clientId }: { platform: Platform; start: string; end: string; config: any; clientId: number }) {
  const { t, lang } = useLang();
  const isMobile = useMobile();
  const account = platform === 'google' ? config?.platforms?.gads?.customerId : config?.platforms?.meta?.accountId;
  const storeKey = `poyais_camp_type_${platform}`;
  const [type, setType] = useState<string>(() => { try { return sessionStorage.getItem(storeKey) || 'ALL'; } catch { return 'ALL'; } });
  const [daily, setDaily] = useState<any[]>([]);
  const [camps, setCamps] = useState<any[]>([]);
  const [cur, setCur] = useState('TRY');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [showIdle, setShowIdle] = useState(false);
  const [statusF, setStatusF] = useState<'all' | 'active' | 'paused'>('all');
  const metrics: Metric[] = platform === 'google'
    ? [{ key: 'spend', label: 'Harcama', kind: 'money' }, { key: 'conversions', label: 'Dönüşüm', kind: 'num' }, { key: 'revenue', label: 'Dönüşüm değeri', kind: 'money' },
       { key: 'roas', label: 'ROAS', kind: 'roas' }, { key: 'clicks', label: 'Tıklama', kind: 'num' }, { key: 'impressions', label: 'Gösterim', kind: 'num' }]
    : [{ key: 'spend', label: 'Harcama', kind: 'money' }, { key: 'conversions', label: 'Satın alma', kind: 'num' }, { key: 'revenue', label: 'Gelir', kind: 'money' },
       { key: 'roas', label: 'ROAS', kind: 'roas' }, { key: 'clicks', label: 'Tıklama', kind: 'num' }, { key: 'impressions', label: 'Gösterim', kind: 'num' }];
  const [sel, setSel] = useState<string[]>(['spend', 'revenue']);

  useEffect(() => { try { sessionStorage.setItem(storeKey, type); } catch {} }, [type, storeKey]);

  useEffect(() => {
    if (!account) return;
    let alive = true;
    setLoading(true); setErr('');
    const qs = `start_date=${start}&end_date=${end}`;
    const base = platform === 'google' ? `${API}/gads/${account}` : `${API}/meta/${account}`;
    Promise.all([
      axios.get(`${base}/daily-campaigns?${qs}`).then(r => r.data || []),
      axios.get(`${base}/campaigns?${qs}`).then(r => r.data || []),
      axios.get(`${API}/analysis/context/${clientId}?${qs}`).then(r => r.data).catch(() => null),
    ]).then(([d, c, ctx]) => {
      if (!alive) return;
      setDaily(Array.isArray(d) ? d : []); setCamps(Array.isArray(c) ? c : []);
      setCur((ctx?.currencies?.[platform === 'google' ? 'gads' : 'meta']) || 'TRY');
    }).catch(e => { if (alive) setErr(e?.response?.data?.detail || t('Veriler alınamadı.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [platform, account, start, end, clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kampanya → tür
  const typeOf = useMemo(() => {
    const m: Record<string, string> = {};
    if (platform === 'google') camps.forEach(c => { m[String(c.campaign_id)] = c.channel_type || 'OTHER'; });
    daily.forEach(d => {
      const id = String(d.campaign_id);
      if (!m[id]) m[id] = platform === 'google' ? (d.campaign_type || 'OTHER') : (M_OBJ[d.campaign_type] || 'OTHER');
    });
    return m;
  }, [platform, camps, daily]);
  const typeDefs = platform === 'google' ? G_TYPES : M_TYPES;
  const typeLabel = (k: string) => t((typeDefs.find(x => x[0] === k) || [k, 'Diğer'])[1]);

  const segItems = useMemo(() => {
    const counts: Record<string, number> = {};
    const ids = new Set<string>([...camps.map(c => String(c.campaign_id)), ...daily.map(d => String(d.campaign_id))]);
    ids.forEach(id => { const k = typeOf[id] || 'OTHER'; counts[k] = (counts[k] || 0) + 1; });
    const items = [{ id: 'ALL', label: t('Tümü'), count: ids.size }];
    typeDefs.forEach(([k, l]) => { if (counts[k]) items.push({ id: k, label: t(l), count: counts[k] }); });
    if (counts.OTHER) items.push({ id: 'OTHER', label: t('Diğer'), count: counts.OTHER });
    return items;
  }, [camps, daily, typeOf, typeDefs, t]);
  useEffect(() => { if (!loading && !segItems.some(s => s.id === type)) setType('ALL'); }, [segItems, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const inType = (id: any) => type === 'ALL' || (typeOf[String(id)] || 'OTHER') === type;

  // Günlük seri
  const series = useMemo(() => {
    const byDate: Record<string, any> = {};
    daily.filter(d => inType(d.campaign_id)).forEach(d => {
      const r = byDate[d.date] = byDate[d.date] || { date: d.date, spend: 0, revenue: 0, conversions: 0, clicks: 0, impressions: 0 };
      r.spend += d.spend || 0; r.revenue += d.revenue || 0; r.conversions += d.conversions || 0; r.clicks += d.clicks || 0; r.impressions += d.impressions || 0;
    });
    return Object.values(byDate).sort((a: any, b: any) => a.date.localeCompare(b.date)).map((r: any) => ({ ...r, roas: r.spend > 0 ? r.revenue / r.spend : null }));
  }, [daily, type, typeOf]); // eslint-disable-line react-hooks/exhaustive-deps
  const totals = useMemo(() => {
    const s = series.reduce((a: any, r: any) => { ['spend', 'revenue', 'conversions', 'clicks', 'impressions'].forEach(k => { a[k] += r[k]; }); return a; },
      { spend: 0, revenue: 0, conversions: 0, clicks: 0, impressions: 0 });
    return { ...s, roas: s.spend > 0 ? s.revenue / s.spend : null };
  }, [series]);
  const fmtM = (m: Metric, v: any, short = false) => m.kind === 'money' ? money(v, cur, short) : m.kind === 'roas' ? roasTxt(v) : num(v, m.key === 'conversions' ? 1 : 0);
  const toggle = (k: string) => setSel(s => s.includes(k) ? (s.length > 1 ? s.filter(x => x !== k) : s) : [s[s.length - 1], k].slice(-2));
  const dlabel = (d: string) => { const [, m, day] = d.split('-'); return `${Number(day)} ${(lang === 'en' ? MONTHS_EN : MONTHS)[Number(m) - 1]}`; };
  const COLORS = ['#C9A84C', '#3b82f6'];   // recharts SVG çizgileri CSS değişkenini okumuyor

  // Tür dağılımı (yalnızca Tümü)
  const mix = useMemo(() => {
    const by: Record<string, number> = {};
    daily.forEach(d => { const k = typeOf[String(d.campaign_id)] || 'OTHER'; by[k] = (by[k] || 0) + (d.spend || 0); });
    const tot = Object.values(by).reduce((a, b) => a + b, 0);
    return { tot, rows: Object.entries(by).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]) };
  }, [daily, typeOf]);

  // Tablo satırları
  const rows = useMemo(() => {
    const ql = q.trim().toLocaleLowerCase('tr');
    return camps.filter(c => inType(c.campaign_id))
      .filter(c => !ql || String(c.campaign_name || '').toLocaleLowerCase('tr').includes(ql))
      .filter(c => { const act = c.status === 'ENABLED' || c.effective_status === 'ACTIVE';
        return (statusF === 'all' || (statusF === 'active' ? act : !act)) && (showIdle || (c.spend || 0) > 0 || act); })
      .map(c => ({ ...c, _type: typeOf[String(c.campaign_id)] || 'OTHER', _ctr: c.impressions ? (c.clicks / c.impressions) * 100 : null,
        _cpa: platform === 'google' ? (c.conversions ? c.spend / c.conversions : null) : null, _res: (c.results || [])[0] || null }));
  }, [camps, q, statusF, showIdle, type, typeOf, platform]); // eslint-disable-line react-hooks/exhaustive-deps

  const bidText = (c: any) => {
    const b = t(BIDDING[c.bidding_strategy] || c.bidding_strategy || '');
    if (c.target_roas) return `${b} · ${roasTxt(c.target_roas)}`;
    if (c.target_cpa) return `${b} · ${money(c.target_cpa, cur)}`;
    if (c.target_impression_share) return `${b} · %${num(c.target_impression_share, 1)}`;
    return b;
  };
  const totalSpend = rows.reduce((a, r) => a + (r.spend || 0), 0);

  const cols: Col<any>[] = platform === 'google' ? [
    { key: 'campaign_name', label: t('Kampanya'), render: r => <NameCell name={r.campaign_name} sub={`${typeLabel(r._type)}${r.bidding_strategy ? ' · ' + bidText(r) : ''}`} /> },
    { key: 'status', label: t('Durum'), render: r => <StatusPill s={r.status} /> },
    { key: 'spend', label: t('Harcama'), align: 'right', mono: true, render: r => money(r.spend, cur) },
    { key: 'impressions', label: t('Gösterim'), align: 'right', mono: true, render: r => num(r.impressions) },
    { key: 'clicks', label: t('Tıklama'), align: 'right', mono: true, render: r => num(r.clicks) },
    { key: '_ctr', label: t('Tıklama oranı'), align: 'right', mono: true, render: r => pct(r._ctr, 2) },
    { key: 'conversions', label: t('Dönüşüm'), align: 'right', mono: true, render: r => num(r.conversions, 1) },
    { key: 'revenue', label: t('Dönüşüm değeri'), align: 'right', mono: true, render: r => money(r.revenue, cur) },
    { key: 'roas', label: 'ROAS', align: 'right', mono: true, render: r => <span style={{ fontWeight: 800, color: roasColor(r.roas) }}>{roasTxt(r.roas)}</span> },
    { key: '_cpa', label: t('Dönüşüm başı maliyet'), align: 'right', mono: true, render: r => money(r._cpa, cur) },
  ] : [
    { key: 'campaign_name', label: t('Kampanya'), render: r => <NameCell name={r.campaign_name} sub={typeLabel(r._type)} /> },
    { key: 'effective_status', label: t('Durum'), render: r => <StatusPill s={r.effective_status || r.status} /> },
    { key: 'spend', label: t('Harcama'), align: 'right', mono: true, render: r => money(r.spend, cur) },
    { key: 'reach', label: t('Erişim'), align: 'right', mono: true, render: r => num(r.reach) },
    { key: 'clicks', label: t('Tıklama'), align: 'right', mono: true, render: r => num(r.clicks) },
    { key: '_ctr', label: t('Tıklama oranı'), align: 'right', mono: true, render: r => pct(r._ctr, 2) },
    { key: '_res', label: t('Sonuç'), align: 'right', value: r => r._res?.count ?? null, render: r => r._res ? <span><span style={{ fontFamily: MONO, fontWeight: 700 }}>{num(r._res.count)}</span> <span style={{ color: T.textDim, fontSize: 11.5 }}>{r._res.label}</span></span> : '—' },
    { key: 'purchases', label: t('Satın alma'), align: 'right', mono: true, render: r => num(r.purchases) },
    { key: 'revenue', label: t('Gelir'), align: 'right', mono: true, render: r => money(r.revenue, cur) },
    { key: 'roas', label: 'ROAS', align: 'right', mono: true, render: r => <span style={{ fontWeight: 800, color: roasColor(r.roas) }}>{roasTxt(r.roas)}</span> },
  ];

  const doExport = () => exportCsv(`${platform === 'google' ? 'google-ads' : 'meta-ads'}-kampanyalar-${start}-${end}.csv`, rows.map(r => ({ ...r, _type: typeLabel(r._type), _res: r._res ? `${r._res.count} ${r._res.label}` : '' })),
    cols.map(c => ({ key: c.key, label: c.label })));

  const smallBtn: React.CSSProperties = { padding: '7px 12px', borderRadius: 9, border: `1px solid ${T.borderMid}`, background: T.surface, fontSize: 12, fontWeight: 700, color: T.textMidAlt, cursor: 'pointer', fontFamily: 'inherit' };

  if (!account) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: 'Manrope, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: T.textDim }}>{t('Kampanya Türü')}</span>
        <div style={{ flex: 1, minWidth: 0 }}><Segments items={segItems} value={type} onChange={setType} /></div>
      </div>

      {err && <Panel><div style={{ color: T.redText, fontSize: 13 }}>{err}</div></Panel>}

      {/* Grafik — Google Ads panelindeki gibi: metrik kutucukları seçilir, en fazla iki çizgi */}
      <section style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : `repeat(${metrics.length}, 1fr)`, borderBottom: `1px solid ${T.tableDivider}` }}>
          {metrics.map((m, i) => {
            const on = sel.indexOf(m.key);
            return (
              <button key={m.key} onClick={() => toggle(m.key)} aria-pressed={on >= 0}
                style={{ textAlign: 'left', padding: '14px 18px', border: 'none', borderRight: !isMobile && i < metrics.length - 1 ? `1px solid ${T.tableDivider}` : 'none',
                  borderBottom: isMobile && i < metrics.length - 2 ? `1px solid ${T.tableDivider}` : 'none',
                  borderTop: `3px solid ${on >= 0 ? COLORS[on] : 'transparent'}`, background: on >= 0 ? T.subtleBg : 'transparent', cursor: 'pointer', fontFamily: 'inherit' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: T.textMidAlt }}>{t(m.label)}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: T.text, fontFamily: MONO, marginTop: 6 }}>{loading ? '…' : fmtM(m, (totals as any)[m.key], true)}</div>
              </button>
            );
          })}
        </div>
        <div style={{ padding: '14px 12px 8px 4px', height: isMobile ? 220 : 280 }}>
          {loading ? <Loading /> : series.length === 0 ? <Muted><div style={{ padding: '80px 16px', textAlign: 'center' }}>{t('Bu dönemde bu türde kampanya yok.')}</div></Muted> : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="var(--py-table-divider)" vertical={false} />
                <XAxis dataKey="date" tickFormatter={dlabel} tick={{ fontSize: 11, fill: 'var(--py-text-dim)' }} axisLine={false} tickLine={false} minTickGap={24} />
                {sel.map((k, i) => {
                  const m = metrics.find(x => x.key === k)!;
                  return <YAxis key={k} yAxisId={k} orientation={i === 0 ? 'left' : 'right'} tick={{ fontSize: 11, fill: 'var(--py-text-dim)' }} axisLine={false} tickLine={false} width={58}
                    tickFormatter={(v: number) => fmtM(m, v, true)} />;
                })}
                <Tooltip labelFormatter={(d: any) => dlabel(String(d))} formatter={(v: any, k: any) => { const m = metrics.find(x => x.key === k)!; return [fmtM(m, v), t(m.label)]; }}
                  contentStyle={{ background: 'var(--py-surface)', border: '1px solid var(--py-border)', borderRadius: 10, fontSize: 12 }} />
                {sel.map((k, i) => <Line key={k} yAxisId={k} dataKey={k} type="monotone" stroke={COLORS[i]} strokeWidth={2.2} dot={false} connectNulls isAnimationActive={false} />)}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        <div style={{ padding: '0 18px 12px' }}><Muted>{t('Grafikte görmek istediğin en fazla iki metriği seç.')}</Muted></div>
      </section>

      {type === 'ALL' && mix.rows.length > 1 && (
        <Panel title={t('Harcama Dağılımı')} sub={t('Kampanya türlerine göre bu dönemin harcaması.')}>
          <div style={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', background: T.trackBg }}>
            {mix.rows.map(([k, v], i) => <div key={k} title={`${typeLabel(k)} · ${money(v, cur)}`} style={{ width: `${(v / mix.tot) * 100}%`, background: TYPE_COLORS[i % TYPE_COLORS.length] }} />)}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 22px', marginTop: 14 }}>
            {mix.rows.map(([k, v], i) => (
              <button key={k} onClick={() => setType(k)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: TYPE_COLORS[i % TYPE_COLORS.length] }} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.text }}>{typeLabel(k)}</span>
                <span style={{ fontSize: 12, color: T.textDim, fontFamily: MONO }}>{money(v, cur, true)} · %{num((v / mix.tot) * 100, 1)}</span>
              </button>
            ))}
          </div>
        </Panel>
      )}

      <Panel pad={false} title={`${t('Kampanyalar')}${type !== 'ALL' ? ' · ' + typeLabel(type) : ''}`}
        sub={platform === 'google' ? t('Dönüşüm sütunları yalnızca kampanyanın teklifte kullandığı dönüşüm hedeflerini sayar.') : t('Satın alma ve gelir Meta pikselinin satın alma olayıdır; "Sonuç" kampanyanın kendi optimizasyon hedefidir. İkisi toplanmaz.')}
        right={<>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('Kampanya ara…')} aria-label={t('Kampanya ara…')}
            style={{ ...smallBtn, fontWeight: 500, width: isMobile ? '100%' : 200, cursor: 'text', color: T.text }} />
          <select value={statusF} onChange={e => setStatusF(e.target.value as any)} style={smallBtn} aria-label={t('Durum')}>
            <option value="all">{t('Tüm Durumlar')}</option><option value="active">{t('Aktif')}</option><option value="paused">{t('Aktif Değil')}</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textMidAlt, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showIdle} onChange={e => setShowIdle(e.target.checked)} />{t('Harcaması olmayanları da göster')}
          </label>
          <button onClick={doExport} disabled={!rows.length} style={{ ...smallBtn, opacity: rows.length ? 1 : 0.5 }}>{t('Dışa Aktar')}</button>
        </>}>
        {loading ? <Loading /> : (
          <DataTable rows={rows} cols={cols.map(c => c.key === 'spend' ? { ...c, render: (r: any) => (
            <span title={totalSpend ? `%${num((r.spend / totalSpend) * 100, 1)}` : undefined}>{money(r.spend, cur)}</span>) } : c)}
            initialSort="spend" pageSize={15} minWidth={980} mobileCards={false} empty={t('Bu dönemde bu türde kampanya yok.')} />
        )}
      </Panel>

      {platform === 'google' && ['PERFORMANCE_MAX', 'DISPLAY', 'MULTI_CHANNEL'].includes(type) && (
        <GoogleTypeDetail kind={type === 'PERFORMANCE_MAX' ? 'pmax' : type === 'DISPLAY' ? 'display' : 'app'} clientId={clientId} start={start} end={end} cur={cur} />
      )}
    </div>
  );
}
